import { getMe } from '../me'
import { inStoreApp } from '../store-app'
import { forgetCallsHere } from '../calls'
import { forgetLocalReads, getQueue, hasUnsavedChanges, setIdentityPartition } from '../api'
import { clearLogoutMark, setLogoutMark } from '../offline-identity'
import { forgetRemindersHere } from '../reminders'
import { forgetAppCard } from '../lock-card'
import { clear, el, PRIVACY_URL, toast } from '../ui'
import type { MessageBy } from '../contact'
import { saveSignupDraft, signupDraft, tradeFromTag } from '../signup-draft'
import { signupFrom } from '../signup-from'
import { freeMonthsWords } from '../../functions/lib/free-time'
import { whenFirst100Gone } from '../first-100'

// Roundly's own sign-in page (0025, 2026-09-25), shown at boot when nobody is
// signed in. It replaces Cloudflare's page: the Roundly look, a yellow button,
// and an eight-digit code emailed from login@roundly.work. Two steps: the email,
// then the code. A right code starts a session and the page reloads into the
// app (or into "Set up your round" for an email that has no round yet).
//
// These requests go straight to the server, not through the offline queue:
// asking for a code with no signal must say so now, not "send later".

const LAST_EMAIL = 'roundly:last-email'
const RESEND_AFTER_MS = 30_000

function remembered(): string {
  try {
    return localStorage.getItem(LAST_EMAIL) ?? ''
  } catch {
    return ''
  }
}

function remember(email: string): void {
  try {
    localStorage.setItem(LAST_EMAIL, email)
  } catch {
    // private window: the box is simply empty next time
  }
}

async function post(path: string, body: Record<string, unknown>): Promise<{ ok: boolean; status: number; error?: string; code?: string }> {
  let res: Response
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
      signal: AbortSignal.timeout(20_000),
    })
  } catch {
    return { ok: false, status: 0, error: "No signal. Check your connection and try again." }
  }
  let parsed: { error?: unknown; code?: unknown } = {}
  try {
    parsed = (await res.json()) as { error?: unknown; code?: unknown }
  } catch {
    // an answer with no body: the status says enough
  }
  return {
    ok: res.ok,
    status: res.status,
    error: typeof parsed.error === 'string' ? parsed.error : undefined,
    code: typeof parsed.code === 'string' ? parsed.code : undefined,
  }
}

export function mountSignIn(): void {
  const root = document.querySelector<HTMLElement>('#screen')!
  clear(root)
  // The Roundly logo and promise above the card, on Roundly's dark background
  // (Liam, 2026-09-25: the first version looked bare next to the rest).
  const brand = el('div', { class: 'signin-brand' },
    el('img', { src: '/roundly-mark.svg?v=b2', alt: '', class: 'signin-mark', width: '56', height: '56' }),
    el('span', { class: 'signin-word' }, 'Roundly'))
  const card = el('section', { class: 'signin-card' })
  root.append(el('div', { class: 'signin-top' }, brand, el('p', { class: 'signin-tagline' }, tradeFromTag(signupFrom()) ? 'Your round, in one place.' : 'Your window cleaning round, in one place.')), card)
  // Starting a new round from the website: one question first, before the email (Liam 30 Sep 2026, planned with
  // Astra). Answered already on this phone this week: straight to the email.
  if (isStarting() && !signupDraft()) showWayStep(card)
  else showEmailStep(card, remembered())
}

// Arriving from the sign-up page on roundly.work (?start). Never in the iPhone app: it is free and never talks
// about trials or starting a round there (Apple rule 3.1.3(f)).
function isStarting(): boolean {
  return !inStoreApp() && new URLSearchParams(window.location.search).has('start')
}

// The one question before the email. Only asked because the answer changes the round: it is how every send
// button opens (the round's usual way, 0036). "Mostly": nobody uses just one, and one customer can go another
// way later. Kept on this phone (signup-draft.ts) and shown again on the set-up screen to confirm.
const WAY_CHOICES: { value: MessageBy; label: string; detail: string }[] = [
  { value: 'text', label: 'Mostly text', detail: 'Roundly opens your phone’s Messages with the words written. You press send.' },
  { value: 'whatsapp', label: 'Mostly WhatsApp', detail: 'Roundly opens WhatsApp with the words written. You press send.' },
  { value: 'email', label: 'Mostly email', detail: 'Roundly opens your email with the words written. You press send.' },
]

function showWayStep(card: HTMLElement): void {
  clear(card)
  const choices = el('div', { class: 'signin-ways', role: 'group', 'aria-label': 'How do you mostly message customers?' })
  for (const c of WAY_CHOICES) {
    const b = el('button', { type: 'button', class: 'signin-way' }, el('strong', {}, c.label), el('span', {}, c.detail))
    b.addEventListener('click', () => {
      saveSignupDraft({ message_by: c.value })
      showEmailStep(card, remembered())
    })
    choices.append(b)
  }
  const signIn = el('button', { type: 'button', class: 'link-button signin-small' }, 'Already use Roundly? Sign in')
  signIn.addEventListener('click', () => showEmailStep(card, remembered(), true))
  // 6 for the First 100 link while places are left, else 3 (0066, free-time.ts, first-100.ts).
  const heading = el('h2', {}, `Start your free ${freeMonthsWords(signupFrom())}`)
  whenFirst100Gone(signupFrom(), (words) => { heading.textContent = `Start your free ${words}` })
  card.append(
    heading,
    el('p', { class: 'signin-lede' }, 'How do you mostly message customers? Chases and reminders will open that way.'),
    choices,
    el('p', { class: 'signin-small' }, 'You can change it for one customer later.'),
    signIn,
    privacyLine(),
  )
}

function wayLabel(v: MessageBy): string {
  return WAY_CHOICES.find((c) => c.value === v)?.label ?? ''
}

function privacyLine(): HTMLElement {
  return el('p', { class: 'signin-small' }, el('a', { href: PRIVACY_URL, target: '_blank', rel: 'noopener' }, 'How Roundly looks after your information'))
}

function showEmailStep(card: HTMLElement, email: string, justSignIn = false): void {
  clear(card)
  const form = el('form', { class: 'signin-form', novalidate: 'novalidate' })
  const input = el('input', { type: 'email', id: 'signin-email', autocomplete: 'email', inputmode: 'email', autocapitalize: 'off', spellcheck: 'false', required: 'required', placeholder: 'you@example.com' })
  input.value = email
  const err = el('p', { class: 'form-error', role: 'alert' })
  const send = el('button', { type: 'submit', class: 'btn-primary signin-go' }, 'Send me a code')
  form.append(
    el('label', { class: 'signin-label', for: 'signin-email' }, 'Your email'),
    input,
    err,
    send,
  )
  // Arriving from the sign-up page on roundly.work (?start): the same code
  // sign-in, worded as starting the free three months (0028).
  // Never in the iPhone app: it is free and never talks about trials or
  // starting a round there (Apple rule 3.1.3(f)).
  const inApp = inStoreApp()
  const starting = isStarting() && !justSignIn
  // What they answered before this, with a way back to change it.
  const draft = starting ? signupDraft() : null
  const answered: HTMLElement[] = []
  if (draft) {
    const change = el('button', { type: 'button', class: 'link-button' }, 'Change')
    change.addEventListener('click', () => showWayStep(card))
    answered.push(el('p', { class: 'signin-small signin-answered' }, `Messages: ${wayLabel(draft.message_by)} · `, change))
  }
  card.append(
    el('h2', {}, starting ? 'Continue with email' : 'Sign in to Roundly'),
    el('p', { class: 'signin-lede' }, starting ? "No card needed. Put in your email and we'll send you an 8-digit code." : "We'll email you an 8-digit code. No password to remember."),
    ...answered,
    form,
    ...(inApp ? [] : [el('p', { class: 'signin-small' }, starting ? 'Already use Roundly? The same email signs you in.' : "New to Roundly? Put in your email and we'll send you a code to get started.")]),
    privacyLine(),
  )
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    const typed = input.value.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(typed)) {
      err.textContent = 'Type your email address.'
      input.focus()
      return
    }
    err.textContent = ''
    send.disabled = true
    send.textContent = 'Sending…'
    void post('/api/auth/start', { email: typed }).then((r) => {
      if (r.ok) {
        remember(typed)
        showCodeStep(card, typed)
        return
      }
      send.disabled = false
      send.textContent = 'Send me a code'
      err.textContent = r.error ?? "We couldn't send the email. Try again in a minute."
    })
  })
  window.setTimeout(() => input.focus(), 50)
}

function showCodeStep(card: HTMLElement, email: string): void {
  clear(card)
  const form = el('form', { class: 'signin-form', novalidate: 'novalidate' })
  const input = el('input', {
    type: 'text',
    id: 'signin-code',
    inputmode: 'numeric',
    autocomplete: 'one-time-code',
    pattern: '[0-9]*',
    maxlength: '8',
    // Eight digits: without this the box asks for twenty characters' width.
    size: '8',
    required: 'required',
    class: 'signin-code',
    'aria-describedby': 'signin-sent',
  })
  const err = el('p', { class: 'form-error', role: 'alert' })
  const go = el('button', { type: 'submit', class: 'btn-primary signin-go' }, 'Sign in')
  const resend = el('button', { type: 'button', class: 'link-button', disabled: 'disabled' }, 'Send a new code')
  const other = el('button', { type: 'button', class: 'link-button' }, 'Use a different email')
  form.append(el('label', { class: 'signin-label', for: 'signin-code' }, 'The code from the email'), input, err, go)
  card.append(
    el('h2', {}, 'Check your email'),
    el('p', { class: 'signin-lede', id: 'signin-sent' }, `We sent an 8-digit code to ${email}. It works for 10 minutes, here on this phone or computer.`),
    form,
    el('div', { class: 'signin-links' }, resend, other),
    el('p', { class: 'signin-small' }, "Nothing there? Look in your junk or spam folder. The email comes from Roundly, login@roundly.work."),
  )

  // A new code can be asked for after half a minute.
  window.setTimeout(() => resend.removeAttribute('disabled'), RESEND_AFTER_MS)
  resend.addEventListener('click', () => {
    resend.setAttribute('disabled', 'disabled')
    err.textContent = ''
    void post('/api/auth/start', { email }).then((r) => {
      if (r.ok) {
        err.textContent = ''
        card.querySelector('#signin-sent')!.textContent = `We sent a new code to ${email}. The old one no longer works.`
        input.value = ''
        input.focus()
      } else {
        err.textContent = r.error ?? "We couldn't send the email. Try again in a minute."
      }
      window.setTimeout(() => resend.removeAttribute('disabled'), RESEND_AFTER_MS)
    })
  })
  other.addEventListener('click', () => showEmailStep(card, email))

  let busy = false
  const submit = (): void => {
    const code = input.value.replace(/\D/g, '')
    if (code.length !== 8) {
      err.textContent = 'The code is the eight numbers in the email.'
      input.focus()
      return
    }
    if (busy) return
    busy = true
    err.textContent = ''
    go.disabled = true
    go.textContent = 'Signing in…'
    void post('/api/auth/verify', { email, code }).then((r) => {
      if (r.ok) {
        go.textContent = 'Signed in'
        // A log-out made with no signal is settled: this phone has just signed in afresh.
        clearLogoutMark()
        window.location.reload()
        return
      }
      busy = false
      go.disabled = false
      go.textContent = 'Sign in'
      err.textContent = r.error ?? "That didn't work. Try again."
      if (r.code === 'expired') {
        input.value = ''
        resend.removeAttribute('disabled')
      } else {
        input.select()
      }
    })
  }
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    submit()
  })
  // Eight numbers typed or pasted (or filled in by the phone from the
  // email): sign in straight away.
  input.addEventListener('input', () => {
    const digits = input.value.replace(/\D/g, '').slice(0, 8)
    if (digits !== input.value) input.value = digits
    if (digits.length === 8) submit()
  })
  window.setTimeout(() => input.focus(), 50)
}

// Log out (the account menu, and the "not on a round" screen): ends this
// phone's Roundly session, or with `everywhere`, every phone signed in as this
// person. Only a confirmed log out leaves the page: with no signal the phone
// would otherwise look logged out while still signed in (security review
// 2026-09-25).
export async function logOut(everywhere = false): Promise<void> {
  // This phone is signed out from this moment, signal or not: nothing restores it with no signal, and its saved
  // copies go now. Unsent changes stay held for this person (docs/plans/offline/PLAN-v3-changes.md §1-2).
  const unsent = hasUnsavedChanges() || getQueue().length > 0
  if (unsent && !window.confirm('Some changes on this phone haven\u2019t sent yet. They\u2019ll send when you sign back in. Log out anyway?')) return
  setLogoutMark()
  forgetLocalReads(getMe()?.email ?? null)
  // Stop in-flight reads repopulating a round copy after it was cleared, and hold every queue immediately.
  setIdentityPartition(null)
  let ok = false
  try {
    const res = await fetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(everywhere ? { all: true } : {}),
      credentials: 'same-origin',
      signal: AbortSignal.timeout(10_000),
    })
    ok = res.ok
  } catch {
    ok = false
  }
  if (!ok) {
    // No signal: this phone is already logged out; the server is told next time Roundly opens with signal.
    location.reload()
    return
  }
  clearLogoutMark()
  // The server ended this phone's job reminders with the sign-in; the phone
  // lets go of its reminder address too.
  await forgetRemindersHere()
  // And the lock screen card goes, so no customer's name stays on it.
  await forgetAppCard()
  // And it stops ringing for customers' calls.
  await forgetCallsHere()
  location.reload()
}
